// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import clearSearch from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/ClearSearch";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { getFormattedDate, xMask } from "@tps/XDL/Utilities";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isFilterOpen: state.pages.savedOrderList.isFilterOpen,
    getCartsApiParameter: state.pages.savedOrderList.getCartsApiParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListFilterStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    orderTotalContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    orderTotalOperatorGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderTotalAmountGridItem?: GridItemProps;
    orderTotalAmountTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;
    // XNG-Changes
    poNumberGridItem?: GridItemProps;
}

export const savedOrderListFilterStyles: SavedOrderListFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    datesGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    datesContainer: { gap: 10 },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    fromGridItem: { width: 6 },
    toGridItem: { width: 6 },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: {
        css: css`
            padding: 15px 30px;
        `,
        width: [12, 12, 6, 4, 4],
    },
    orderTotalContainer: { gap: 10 },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    orderTotalOperatorGridItem: { width: 6 },
    orderTotalAmountGridItem: { width: 6 },
    orderTotalAmountTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 32px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");
const StyledOrderTotalFieldSet = getStyledWrapper("fieldset");

export const styles = savedOrderListFilterStyles;
let updateTimeoutId: any;
const SavedOrderListFilter = ({ isFilterOpen, getCartsApiParameter, updateSearchFields, clearSearch }: Props) => {
    const [orderSubtotal, setOrderSubtotal] = React.useState("");
    const [orderSubtotalOperator, setOrderSubtotalOperator] = React.useState("");
    // XNG-Changes
    const [poNumber, setPoNumber] = React.useState("");
    const currentPoNumber = (getCartsApiParameter as any)?.poNumber;

    useEffect(() => {
        if (
            !orderSubtotal &&
            !orderSubtotalOperator &&
            getCartsApiParameter.orderSubtotal &&
            getCartsApiParameter.orderSubtotalOperator
        ) {
            setOrderSubtotal(getCartsApiParameter.orderSubtotal);
            setOrderSubtotalOperator(getCartsApiParameter.orderSubtotalOperator);
        }
    }, []);

    useEffect(() => {
        setPoNumber(currentPoNumber || "");
    }, [currentPoNumber]);

    const orderSubtotalChangeHandler = (totalValue: string, totalOperator: string) => {
        if ((totalValue === "" && totalOperator !== "") || (totalValue !== "" && totalOperator === "")) {
            return;
        }

        updateSearchFields({
            orderSubtotal: totalValue,
            orderSubtotalOperator: totalOperator,
        });
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: selectedDay ? selectedDay.toISOString() : "",
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: selectedDay ? selectedDay.toISOString() : "",
        });
    };

    const orderSubtotalValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        // XNG-Changes: prevent -/+ input into type number
        const total = xMask.decimal(event.target.value);
        setOrderSubtotal(total);

        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            orderSubtotalChangeHandler(total, orderSubtotalOperator);
        }, 350);
    };

    const orderSubtotalOperatorChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const operator = event.target.value;
        setOrderSubtotalOperator(operator);

        orderSubtotalChangeHandler(orderSubtotal, operator);
    };

    const resetOrderSubtotal = () => {
        setOrderSubtotal("");
        /**
         * XNG-Changes:
         * OOTB issue: when the tag is removed, the filter input is not cleared
         */
        setOrderSubtotalOperator("");
        updateSearchFields({ orderSubtotalOperator: "", orderSubtotal: "" });
    };

    // XNG-Changes
    const poNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const poNumberEvent = event.target.value;
        setPoNumber(poNumberEvent);

        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                poNumber: poNumberEvent,
            } as any);
        }, 350);
    };

    // XNG-Changes
    const resetPoNumber = () => {
        setPoNumber("");
        updateSearchFields({
            poNumber: "",
        } as any);
    };

    const clearFiltersClickHandler = () => {
        clearSearch();
        setOrderSubtotal("");
        setOrderSubtotalOperator("");
    };

    const searchTag = () => (
        <StyledWrapper {...styles.appliedFiltersContainer}>
            {getCartsApiParameter.fromDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        fromDateChangeHandler({});
                    }}
                >
                    {/* XNG-Change: OOTB Fix Date Format */}
                    {`${translate("From")}: ${getFormattedDate(new Date(getCartsApiParameter.fromDate), true)}`}
                </Tag>
            )}
            {getCartsApiParameter.toDate && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        toDateChangeHandler({});
                    }}
                >
                    {/* XNG-Change: OOTB Fix Date Format */}
                    {`${translate("To")}: ${getFormattedDate(new Date(getCartsApiParameter.toDate), true)}`}
                </Tag>
            )}
            {getCartsApiParameter.orderSubtotalOperator && getCartsApiParameter.orderSubtotal && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        resetOrderSubtotal();
                    }}
                >
                    {`${translate("Order Total")}: ${getCartsApiParameter.orderSubtotalOperator} ${
                        getCartsApiParameter.orderSubtotal
                    }`}
                </Tag>
            )}
            {/* XNG-Change */}
            {poNumber && (
                <Tag
                    {...styles.appliedFilterTag}
                    onDelete={() => {
                        resetPoNumber();
                    }}
                >
                    {`${translate("PO Number")}: ${poNumber}`}
                </Tag>
            )}
        </StyledWrapper>
    );

    if (!isFilterOpen) {
        return <> {searchTag()} </>;
    }

    const fromDate = getCartsApiParameter.fromDate ? new Date(getCartsApiParameter.fromDate) : undefined;
    const toDate = getCartsApiParameter.toDate ? new Date(getCartsApiParameter.toDate) : undefined;

    // XNG-Changes: adding new Date(), show search tags always if toggle is in open and closed status
    return (
        <>
            <GridContainer {...styles.container}>
                <GridItem {...styles.headingGridItem}>
                    <Typography {...styles.heading} as="h2">
                        {translate("Filter")}
                    </Typography>
                </GridItem>
                <GridItem {...styles.datesGridItem}>
                    <StyledDateRangeFieldSet {...styles.dateRangeFieldset}>
                        <VisuallyHidden as="label">{translate("Date Range")}</VisuallyHidden>
                        <GridContainer {...styles.datesContainer}>
                            <GridItem data-test-selector="savedOrderList_fromDateFilter" {...styles.fromGridItem}>
                                <DatePicker
                                    {...styles.fromDate}
                                    label={translate("From")}
                                    selectedDay={fromDate}
                                    onDayChange={fromDateChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        maxDate: toDate || new Date(),
                                        ...styles.fromDate?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                            <GridItem data-test-selector="savedOrderList_toDateFilter" {...styles.toGridItem}>
                                <DatePicker
                                    {...styles.toDate}
                                    label={translate("To")}
                                    selectedDay={toDate}
                                    onDayChange={toDateChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        minDate: fromDate,
                                        maxDate: new Date(),
                                        ...styles.toDate?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledDateRangeFieldSet>
                </GridItem>
                <GridItem {...styles.orderTotalGridItem}>
                    <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                        <VisuallyHidden as="label">{translate("Order Subtotal")}</VisuallyHidden>
                        <GridContainer {...styles.orderTotalContainer}>
                            <GridItem {...styles.orderTotalOperatorGridItem}>
                                <Select
                                    {...styles.orderTotalOperatorSelect}
                                    label={translate("Order Subtotal")}
                                    value={orderSubtotalOperator}
                                    data-test-selector="savedOrderList_comparisonMethod"
                                    onChange={orderSubtotalOperatorChangeHandler}
                                >
                                    <option value="">{translate("Select")}</option>
                                    <option key="Greater Than" value="Greater Than">
                                        {translate("Greater Than")}
                                    </option>
                                    <option key="Less Than" value="Less Than">
                                        {translate("Less Than")}
                                    </option>
                                    <option key="Equal To" value="Equal To">
                                        {translate("Equal To")}
                                    </option>
                                </Select>
                            </GridItem>
                            <GridItem {...styles.orderTotalAmountGridItem}>
                                <TextField
                                    {...styles.orderTotalAmountTextField}
                                    value={orderSubtotal}
                                    type="number"
                                    data-test-selector="savedOrderList_comparisonValue"
                                    onChange={orderSubtotalValueChangeHandler}
                                    placeholder={translate("Amount")}
                                    min={0}
                                    max={9999999999}
                                />
                            </GridItem>
                        </GridContainer>
                    </StyledOrderTotalFieldSet>
                </GridItem>
                {/* XNG-Changes */}
                <GridItem {...styles.poNumberGridItem}>
                    <TextField
                        label={translate("PO Number")}
                        value={poNumber}
                        type="text"
                        data-test-selector="savedOrderList_poNumber"
                        onChange={poNumberChangeHandler}
                        placeholder={translate("PO Number")}
                    />
                </GridItem>
                <GridItem {...styles.buttonsItem}>
                    <Button
                        {...styles.clearFiltersButton}
                        data-test-selector="savedOrderList_clearFiltersButton"
                        onClick={clearFiltersClickHandler}
                        aria-label="Clear Filters"
                    >
                        {translate("Clear Filters")}
                    </Button>
                </GridItem>
            </GridContainer>
            {searchTag()}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SavedOrderListFilter),
    definition: {
        group: "Saved Order List",
        displayName: "Search Results Filter",
        allowedContexts: ["SavedOrderListPage"],
        isSystem: true,
    },
};

export default widgetModule;
